import { Lightning, Colors, AppData, Router } from '@lightningjs/sdk';
import { translate } from 'support/translate';
import Button from 'components/common/buttons/Button';
import { HoverableComponent } from 'components/common/HoverableComponent';
import { constants } from 'aliases';
import { debounce, getImageTextureObj } from 'support/generalUtils';
import { getFontFaceFromStyle } from 'support/textUtils';
import LoginLoadingSpinner from 'components/pages/login/LoginLoadingSpinner';
import OfflineErrorToast from 'components/common/toasts/OfflineErrorToast';
import Keyboard from 'components/common/input/Keyboard';
import { InputChangedData } from 'types/lightning';
import LoginInputField from 'components/common/input/LoginInputField';
import { KeyboardLayouts } from 'components/common/input/Keys';
import ServiceErrorToast from 'components/common/toasts/ServiceErrorToast';
import { LoginRadiusError } from 'types/loginRadius';
import {
  ErrorCode,
  reportError,
  reportLoginSkipped,
} from 'services/analytics/reportingServicePage';
import LoginPage from '../LoginPage';
import { PageId } from 'types/pageId';
import TextLabel from 'components/common/TextLabel';

export interface AbstractLoginModalTemplateSpec
  extends Lightning.Component.TemplateSpec {
  Title: typeof TextLabel;
  Description: typeof TextLabel;
  Header: {
    LogoSection: {
      Logo: Lightning.Texture;
      Text: typeof TextLabel;
    };
    BackButton: typeof Button;
  };
  ServiceErrorToast: typeof ServiceErrorToast;
  OfflineErrorToast: typeof OfflineErrorToast;
  Content: any;
  Footer: {
    Text: typeof TextLabel;
  };
  SkipButton: typeof Button;
  LoadingSpinner: typeof LoginLoadingSpinner;
  Keyboard: typeof Keyboard;
}

const LOGO_ICON = 'static/images/brand-logo-white.svg';
const LOGO_TEXTURE = getImageTextureObj(LOGO_ICON, 182, 80);

const ARROW_LEFT_ICON_UNFOCUSED = getImageTextureObj(
  'static/images/arrow-left-light.svg',
  19,
  19,
);
const ARROW_LEFT_ICON_FOCUSED = getImageTextureObj(
  'static/images/arrow-left-dark.svg',
  19,
  19,
);

const SAFE_OFFSET_X = 80;
const SAFE_OFFSET_Y = 60;
const CONTENT_WIDTH = 1760;
const HEADER_HEIGHT = 124;
const FOOTER_OFFSET_Y = 955;
const SPINNER_X = SAFE_OFFSET_X + CONTENT_WIDTH / 2;
const SPINNER_Y = 1080 / 2;
const SPINNER_SIZE = 104;

const BUTTON_WIDTH = 254;
const BUTTON_HEIGHT = 65;
const BACK_BUTTON_SPACING = 11;
const SKIP_BUTTON_X = SAFE_OFFSET_X + CONTENT_WIDTH - BUTTON_WIDTH;

const FONT_SIZE = 18;

const SERVICE_ERROR_X = 1132;
const SERVICE_ERROR_Y = 60;

const MANUAL_ANNOUNCE_DELAY = constants.timers.manualAnnounceDelay;

const OFFLINE_ERROR_X = 1136;
const OFFLINE_ERROR_Y = 60;

export enum LoginFlow {
  ABSTRACT, // Should not get here
  GET_STARTED,
  SIGN_UP,
  SIGN_IN,
  SIGN_OUT,
}

export default abstract class AbstractLoginModal
  extends HoverableComponent<AbstractLoginModalTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<AbstractLoginModalTemplateSpec>
{
  protected _pageId = PageId.LOGIN;

  protected _showBackButton = false;
  protected _showFooter = true;
  protected _showSkipButton = true;
  private _currentInputField: LoginInputField | null = null;
  protected _isLoading = false;

  protected _Content = this.getByRef('Content')!;
  private _Header = this.getByRef('Header')!;
  private _BackButton = this._Header.getByRef('BackButton')!;
  private _Footer = this.getByRef('Footer')!;
  private _SkipButton = this.getByRef('SkipButton')!;
  private _LoadingSpinner = this.getByRef('LoadingSpinner')!;
  private _ServiceErrorToast = this.getByRef('ServiceErrorToast')!;
  private _OfflineErrorToast = this.getByRef('OfflineErrorToast')!;
  protected _Keyboard = this.getByRef('Keyboard')!;

  private manualAnnounce = debounce(
    this.handleManualAnnounce.bind(this),
    MANUAL_ANNOUNCE_DELAY,
  );

  private handleManualAnnounce(message: string) {
    this.fireAncestors('$announce', message, { append: true });
  }

  private hideServiceError() {
    this._ServiceErrorToast.patch({ alpha: 0 });
  }

  private showServiceError() {
    this._ServiceErrorToast.patch({ alpha: 1 });
  }

  private handleServiceError(error: LoginRadiusError) {
    reportError({ error, userFacing: true, code: ErrorCode.c40 });
    this._ServiceErrorToast.updateLabel(error);
    this.showServiceError();
    this.manualAnnounce(this._ServiceErrorToast.label);
  }

  protected get loginFlow(): LoginFlow {
    return LoginFlow.ABSTRACT;
  }

  set setLoading(isLoading: boolean) {
    this._isLoading = isLoading;
    this._LoadingSpinner.show = isLoading;

    // Do not fully hide the content to avoid triggering life cycle events too early
    this._Content.patch({ alpha: isLoading ? constants.ui.invisible : 1.0 });
  }

  static override _template(): Lightning.Component.Template<AbstractLoginModalTemplateSpec> {
    return {
      alpha: 0,
      rect: true,
      w: 1920,
      h: 1080,
      color: Colors('modalBackground').get(),
      zIndex: constants.ui.zIndex.modalLayer,

      Header: {
        x: SAFE_OFFSET_X,
        y: SAFE_OFFSET_Y,
        w: CONTENT_WIDTH,
        h: HEADER_HEIGHT,
        flex: {
          justifyContent: 'center',
        },
        LogoSection: {
          flex: {
            direction: 'column',
            alignItems: 'center',
          },
          Logo: {
            ...LOGO_TEXTURE,
            flexItem: { marginBottom: 24 },
          },
          Text: {
            type: TextLabel,
            text: {
              text: translate('account.modal.logoText'),
              fontSize: 28,
              letterSpacing: 0.25,
              lineHeight: 28,
              fontFace: getFontFaceFromStyle('bold'),
            },
          },
        },
        BackButton: {
          type: Button,
          flexItem: false,
          visible: false,
          label: translate('account.modal.back'),
          minWidth: BUTTON_WIDTH,
          height: BUTTON_HEIGHT,
          fontSize: FONT_SIZE,
          margin: BACK_BUTTON_SPACING,
          startIcon: {
            focused: ARROW_LEFT_ICON_FOCUSED,
            unfocused: ARROW_LEFT_ICON_UNFOCUSED,
          },
          action: '$back',
          zIndex: constants.ui.zIndex.foregroundLayer + 2,
          signals: {
            $back: '$back',
            $onHover: '$onHover',
          },
        },
      },
      ServiceErrorToast: {
        type: ServiceErrorToast,
        x: SERVICE_ERROR_X,
        y: SERVICE_ERROR_Y,
        alpha: 0,
      },
      OfflineErrorToast: {
        type: OfflineErrorToast,
        x: OFFLINE_ERROR_X,
        y: OFFLINE_ERROR_Y,
        alpha: constants.ui.invisible, // To have the image preload early
        zIndex: constants.ui.zIndex.spinnerLayer,
        signals: {
          $onHover: false,
        },
      },
      Content: {
        x: SAFE_OFFSET_X,
        w: CONTENT_WIDTH,
        flex: {
          direction: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        },
      },
      Footer: {
        x: SAFE_OFFSET_X,
        y: FOOTER_OFFSET_Y,
        h: BUTTON_HEIGHT,
        flex: {
          alignItems: 'flex-end',
        },
        Text: {
          type: TextLabel,
          text: {
            text: translate('account.modal.footer'),
            fontSize: FONT_SIZE,
            fontFace: getFontFaceFromStyle('bold'),
          },
        },
      },
      SkipButton: {
        type: Button,
        x: SKIP_BUTTON_X,
        y: FOOTER_OFFSET_Y,
        visible: false,
        label: translate('account.modal.skip'),
        minWidth: BUTTON_WIDTH,
        height: BUTTON_HEIGHT,
        fontSize: FONT_SIZE,
        action: '$skip',
        zIndex: constants.ui.zIndex.foregroundLayer + 2,
        signals: {
          $skip: '$skip',
          $onHover: '$onHover',
        },
      },
      LoadingSpinner: {
        type: LoginLoadingSpinner,
        x: SPINNER_X,
        y: SPINNER_Y,
        size: SPINNER_SIZE,
        show: false,
      },
      Keyboard: {
        type: Keyboard,
        currentLayout: KeyboardLayouts.LOWERCASE_ABC,
        signals: {
          hideKeyboard: '_onHideKeyboard',
          inputChanged: '_onInputChanged',
          inputComplete: '_onInputComplete',
          $onHover: true,
        },
        zIndexLocal: constants.ui.zIndex.foregroundLayer + 3,
      },
    };
  }

  private $back() {
    AppData?.modalService.close();
  }

  private $skip() {
    reportLoginSkipped(this._pageId, this.loginFlow);

    this.closeAllLogin();
  }

  private $openKeyboard(target: LoginInputField) {
    this._currentInputField = target;
    this._currentInputField.isKeyboardActive = () => this._Keyboard.hasFocus();
    this._setState('Keyboard');
  }

  private _onHideKeyboard() {
    this._setState('Content');
  }

  private _onInputChanged(event: InputChangedData) {
    const { input } = event;

    if (this._currentInputField) {
      this._currentInputField.input = input;
    }
  }

  private _onInputComplete() {
    this._onHideKeyboard();
  }

  override _handleKey() {
    return true;
  }

  override _captureKey() {
    return this._isLoading;
  }

  override _onActive() {
    this._BackButton.patch({ visible: this._showBackButton });
    this._Footer.patch({ visible: this._showFooter });
    this._SkipButton.patch({ visible: this._showSkipButton });

    this._setState(
      this._Content.children.length > 0
        ? 'Content'
        : this._showBackButton
        ? 'Header'
        : 'SkipButton',
    );

    this._resetOfflineToast();
  }

  override _handleHover(target: any) {
    return true;
  }

  override _handleBack() {
    AppData?.modalService.close();
  }

  private _onOffline() {
    this._OfflineErrorToast.patch({ alpha: 1 });
  }

  private _backOnline() {
    this._OfflineErrorToast.patch({ alpha: 0.01 }); //make sure image reloads while online
  }

  // TODO: This will needed to be moved/refactored to happen everywhere
  // except playback at a later date
  private _resetOfflineToast() {
    AppData!.offlineErrorService.addOfflineAction(
      'offline',
      this._onOffline.bind(this),
    );

    AppData!.offlineErrorService.resumeOnlineAction(
      'offline',
      this._backOnline.bind(this),
    );

    AppData!.offlineErrorService.isOffline()
      ? this._onOffline()
      : this._backOnline();
  }

  override _inactive() {
    this.hideServiceError();
  }

  protected handleFieldLevelError(error: LoginRadiusError) {
    // override
  }

  protected $onHover(target: Lightning.Component) {
    switch (target) {
      case this._BackButton:
        this._setState('Header');
        break;
      case this._SkipButton:
        this._setState('SkipButton');
        break;
    }
  }

  async onLoading<T>(promise: Promise<T>) {
    this.hideServiceError();
    this.setLoading = true;
    try {
      const result = await promise;
      this.setLoading = false;
      return result;
    } catch (error) {
      this.setLoading = false;
      this.handleLoadingError(error as LoginRadiusError);
    }
  }

  hide() {
    this.patch({ alpha: 0 });
  }

  show() {
    this.patch({ alpha: 1 });
    this._resetOfflineToast();
  }

  handleLoadingError(error: LoginRadiusError) {
    this.handleServiceError(error);
  }

  protected closeAllLogin() {
    const activePage = Router.getActivePage() as LoginPage;
    activePage.onLoginExit();
  }

  private $resetToEmailInput() {
    AppData?.modalService.closeUntil('EmailInputModal');
  }

  static override _states() {
    return [
      class Header extends this {
        override _handleDown() {
          this._setState('Content');
        }

        override _getFocused() {
          return this._BackButton;
        }
      },
      class Content extends this {
        override _handleUp() {
          if (this._showBackButton) this._setState('Header');
        }

        override _handleDown() {
          if (this._showSkipButton) this._setState('SkipButton');
        }
      },
      class SkipButton extends this {
        override _handleUp() {
          this._setState('Content');
        }

        override _getFocused() {
          return this._SkipButton;
        }
      },
      class Keyboard extends this {
        override $enter() {
          if (this._currentInputField) {
            const inputField = this._currentInputField.inputField;

            this._Keyboard.patch({
              inputField: inputField,
            });

            this._currentInputField.patch({ toggleCursor: true });
            inputField._update(inputField.input.length);
          }
        }

        override $exit() {
          if (this._currentInputField) {
            const inputField = this._currentInputField.inputField;
            this._currentInputField.patch({ toggleCursor: false });
            this._currentInputField.inputField._update(inputField.input.length);
          }
        }

        override _getFocused() {
          return this._Keyboard;
        }

        override _handleBack() {
          this._setState('Content');
        }

        override _handleUp() {
          this._setState('Content');
        }

        override _handleDown() {
          this._setState('Content');
        }
      },
    ];
  }
}
